import { withAccountConfirmation } from "../account-confirmation";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, idem, setCsrfToken, unwrap } from "../client";
import { qk } from "../keys";

/**
 * Loads the signed-in principal and arms the session's CSRF token, so any page that loads `me` can make
 * cookie-authenticated changes, including pages outside the workspace shell (device sign-in, approvals, invitations).
 */
export async function fetchMe() {
  const me = await unwrap(api.GET("/v1/me"));
  setCsrfToken(me.csrf_token ?? null);
  return me;
}

/** The signed-in principal, their scopes and workspace memberships. */
export function useMe() {
  return useQuery({
    queryKey: qk.me(),
    queryFn: fetchMe,
    staleTime: 5 * 60_000,
  });
}

export function useWorkspaces() {
  return useQuery({
    queryKey: qk.workspaces(),
    queryFn: () => unwrap(api.GET("/v1/workspaces")),
    staleTime: 5 * 60_000,
  });
}

export function useWorkspace(workspace: string) {
  return useQuery({
    queryKey: qk.workspace(workspace),
    queryFn: () => unwrap(api.GET("/v1/workspaces/{workspace}", { params: { path: { workspace } } })),
    enabled: !!workspace,
  });
}

export function useMembers(workspace: string) {
  return useQuery({
    queryKey: qk.members(workspace),
    queryFn: () => unwrap(api.GET("/v1/workspaces/{workspace}/members", { params: { path: { workspace } } })),
    enabled: !!workspace,
  });
}

/** Which terminal is asking to sign in with this code (the CLI device flow). */
export function useDeviceLookup(userCode: string) {
  return useQuery({
    queryKey: ["device", userCode],
    queryFn: () => unwrap(api.GET("/v1/auth/device", { params: { query: { user_code: userCode } } })),
    enabled: !!userCode,
    retry: false,
  });
}

/** Approve or deny a CLI sign-in for the signed-in user. */
export function useDecideDevice() {
  return useMutation({
    mutationFn: (body: { user_code: string; decision: "approve" | "deny" }) =>
      withAccountConfirmation(`device:approve:${body.user_code.toUpperCase()}`, (headers) => unwrap(api.POST("/v1/auth/device/decision", { headers, params: { header: idem() }, body }))),
  });
}
